<script setup>
  import {computed, inject, onMounted, ref, watch} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import LaborTableRow from "@pages/Records/Components/Sections/LaborBar/Components/Row.vue";
  import {mainLaborvalueList} from "@pages/Records/Components/Sections/LaborBar/enums.js";
  import {MedButton, MedCheckbox} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {sortList} from "@utils/Helpers/ListSorter.js";
  import {cloneDeep, debounce, isEqual} from "es-toolkit";
  import Sortable from "sortablejs";

  defineOptions({
    name: "LaborTable",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      default: false,
    },
    recordLaborValues: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: null,
    },
  });

  defineEmits(["update"]);

  const page = usePage();

  const setIsMassUpdateProcessing = inject("setIsMassUpdateProcessing");
  const deleteDialog = inject("recordEditDeleteRowDialog");

  const refDraggable = ref(null);

  const isDragging = ref(false);
  const laborValues = ref([...props.recordLaborValues]);
  const pendingSortOrder = ref(null);
  const sortable = ref(null);
  const sortCancelToken = ref(null);
  const selectedRows = ref([]);

  const getLaborValueIds = (items) => items.map((laborValue) => laborValue.id);

  const sortDirection = computed(() => {
    if (laborValues.value.length > 0) {
      const copyOfLaborValues = cloneDeep(laborValues.value).filter((laborValue) => laborValue.id !== null);

      const laborValueIds = copyOfLaborValues.map((laborValue) => laborValue.id);
      const ascSortedObject = sortList(copyOfLaborValues, "type", "asc");

      if (isEqual(laborValueIds, ascSortedObject.sortedIds)) {
        return "asc";
      }

      // avoid calling mutating reverse() method on original array
      if (isEqual(laborValueIds, ascSortedObject.sortedIds.toReversed())) {
        return "desc";
      }
    }

    return "";
  });

  const newSortDirection = computed(() => {
    if (sortDirection.value === "" || sortDirection.value === "desc") {
      return "asc";
    }

    if (sortDirection.value === "asc") {
      return "desc";
    }

    return "";
  });

  watch(
    () => props.recordLaborValues,
    (newLaborValues) => {
      const incomingSortOrder = getLaborValueIds(newLaborValues);

      if (
        pendingSortOrder.value !== null &&
        newLaborValues.length === laborValues.value.length &&
        !isEqual(incomingSortOrder, pendingSortOrder.value)
      ) {
        return;
      }

      laborValues.value = [...newLaborValues];

      if (pendingSortOrder.value !== null && isEqual(incomingSortOrder, pendingSortOrder.value)) {
        pendingSortOrder.value = null;
      }
    },
  );

  watch(
    () => laborValues.value,
    (value, oldValue) => {
      if (value.length !== oldValue?.length) {
        initSortable();
      }

      // remove deleted ids from selectedRows
      const newIds = new Set(value.map((laborValue) => laborValue.id));
      selectedRows.value = selectedRows.value.filter((id) => newIds.has(id));
    },
  );

  onMounted(() => {
    initSortable();
  });

  const updateOrder = (newSortOrder) => {
    const currentSortOrder = getLaborValueIds(laborValues.value);

    if (isEqual(currentSortOrder, newSortOrder)) {
      return;
    }

    pendingSortOrder.value = [...newSortOrder];

    laborValues.value = laborValues.value.toSorted((a, b) => {
      return newSortOrder.indexOf(a.id) - newSortOrder.indexOf(b.id);
    });

    if (sortCancelToken.value) {
      sortCancelToken.value.cancel();
    }

    // lock immediately (not inside the debounced request) so adding/removing a
    // laborValue can't race with the pending sort request and desync the ids snapshot
    setIsMassUpdateProcessing("laborValue", true);

    updateOrderRequest(newSortOrder);
  };

  const updateOrderRequest = debounce((newSortOrder) => {
    router.post(
      route("sort.store", {
        patient: page.props.patient.id,
        record: page.props.record.id,
        relation: "laborvalues",
      }),
      {
        ids: newSortOrder,
      },
      {
        preserveScroll: true,
        only: ["flash", "laborvalues"],
        onFinish: () => {
          setIsMassUpdateProcessing("laborValue", false);
        },
        onCancelToken: (cancelToken) => (sortCancelToken.value = cancelToken),
      },
    );
  }, 2000);

  const sortByName = () => {
    if (!props.isEditable) return;

    const sortedObject = sortList(laborValues.value, "type", newSortDirection.value);
    const sortedIds = sortedObject.sortedIds.filter((laborValueId) => laborValueId !== null);

    updateOrder(sortedIds);
  };

  const initSortable = () => {
    if (sortable.value !== null) {
      return; // already initialized
    }

    if (refDraggable.value === null) {
      return; // refDraggable is not available yet
    }

    sortable.value = Sortable.create(refDraggable.value, {
      handle: "[drag-handle]",
      draggable: "[drag-item]",
      direction: "vertical",
      ghostClass: "sortable-ghost",
      dataIdAttr: "drag-item", // requires `drag-item="{{item.id}}"`
      store: {
        /**
         * Save the order of elements. Called onEnd (when the item is dropped).
         * @param {Sortable} sortable
         */
        set: (sortable) => {
          // sort medications according to new sort order
          const mainLaborValues = laborValues.value
            .filter((laborvalue) => mainLaborvalueList.includes(laborvalue.mc_laborvalue_key) && laborvalue.id !== null)
            .map((laborValue) => laborValue.id);

          updateOrder([...mainLaborValues, ...sortable.toArray()]);
        },
      },
      onStart: () => {
        isDragging.value = true;
      },
      onEnd: () => {
        isDragging.value = false;
      },
    });
  };

  const showDeleteConfirmDialog = () => {
    deleteDialog({routeName: "laborvalues", payload: {laborvalue: selectedRows.value}});
  };

  const selectAllState = computed({
    get: () => {
      if (selectedRows.value.length === laborValues.value.length) return true;
      if (selectedRows.value.length > 0) return "indeterminate";
      return false;
    },
    set: (value) => {
      selectedRows.value = value ? laborValues.value.map((l) => l.id) : [];
    },
  });

  const toggleRow = (id) => {
    const index = selectedRows.value.indexOf(id);
    if (index !== -1) {
      selectedRows.value.splice(index, 1);
    } else {
      selectedRows.value.push(id);
    }
  };
</script>

<template>
  <!-- ☑️ multiselect actions -->
  <section v-if="mode === 'multi-select' && selectedRows?.length" class="items-top mb-2 flex space-x-1">
    <div class="my-2 rounded border-2 border-transparent bg-gray-200 px-1 py-0 text-xs font-semibold text-gray-700">
      {{ selectedRows?.length }}
    </div>

    <div class="flex flex-wrap gap-0.5">
      <MedButton
        flat
        icon="delete"
        :data-test="getTestId('default-head-bulk-delete-button')"
        @click="showDeleteConfirmDialog()"
      >
        Löschen
      </MedButton>
    </div>
  </section>

  <div class="grid grid-cols-13 items-end gap-x-6 pb-2 text-sm font-normal text-gray-700">
    <div class="col-span-3 flex items-center">
      <template v-if="isEditable">
        <div class="flex size-6 shrink-0 items-center justify-center stroke-0">
          <MedCheckbox
            v-if="mode === 'multi-select'"
            v-model="selectAllState"
            aria-label="Alle Zeilen auswählen"
            variant="primary"
            :data-test="getTestId('table-select-all-checkbox')"
          />
        </div>

        <button
          :class="cn('ml-2')"
          class="flex items-center text-gray-700 hover:no-underline"
          @click.prevent="sortByName"
        >
          <template v-if="sortDirection === 'asc'">
            <ComponentIcon>expand_more</ComponentIcon>
          </template>

          <template v-else-if="sortDirection === 'desc'">
            <ComponentIcon>expand_less</ComponentIcon>
          </template>

          <template v-else>
            <ComponentIcon>unfold_more</ComponentIcon>
          </template>

          <span>Was</span>
        </button>
      </template>
      <template v-else>
        <span class="ml-2">Was</span>
      </template>
    </div>
    <div class="col-span-3">Wert (opt.)</div>
    <div class="col-span-2">Einheit (opt.)</div>
    <div class="col-span-2" :class="{'col-span-3': !isEditable}">Abweichung</div>
    <div class="col-span-2" :class="{'col-span-1': isEditable}">Von (Datum)</div>
    <div class="col-span-1" />
  </div>

  <ul ref="refDraggable">
    <template v-for="(laborValue, index) in laborValues" :key="laborValue.id">
      <LaborTableRow
        :drag-item="laborValue.id"
        :is-editable="isEditable"
        :laborvalue="laborValue"
        :is-dragging="isDragging"
        :test-id="getTestId(`table-${index}`)"
        :show-labels="false"
        @update="$emit('update')"
      >
        <template v-if="mode === 'multi-select'" #prefix>
          <div class="flex size-6 shrink-0 items-center justify-center stroke-0">
            <MedCheckbox
              :model-value="selectedRows.includes(laborValue.id)"
              :aria-label="`Zeile ${laborValue.type} auswählen`"
              variant="primary"
              :data-test="getTestId('table-row-select-checkbox')"
              @update:model-value="toggleRow(laborValue.id)"
            />
          </div>
        </template>
      </LaborTableRow>
    </template>
  </ul>
</template>
